/**
 * PrivacyModeList — the notification feed as it appears in privacy
 * ("public") mode: one inert card per app, showing the app icon, its name
 * and an aggregate count. No content, no actions, nothing to click.
 *
 * Split out of `SidePanelNotificationList`, which used to carry both modes
 * behind a `publicMode` flag. The two trees share no rows, no toolbar and no
 * behaviour — privacy mode has no search, no group/timeline toggle, no recap
 * slot, no auto-mark-read, no keyboard navigation and no footer hint. Keeping
 * them in one component meant every one of those had to defend itself with a
 * `!publicMode` term, and the mode leaked into two hooks' options.
 *
 * The host picks the component; neither knows the other exists. `publicMode`
 * lives at the host anyway (it owns the toggle), so the branch is one ternary
 * where the flag already is.
 *
 * One behavioural consequence, accepted deliberately: the notification list now
 * UNMOUNTS when privacy mode turns on, so its session-scoped view state — the
 * search query and the collapsed-apps set — is gone on the way back out. Under
 * the old merged component that state survived the round-trip. Returning to a
 * clean, unfiltered view after a screen-share is the better default anyway, and
 * it matches `publicMode` itself resetting to OFF on every restart.
 *
 * Deliberately preserved from the merged version: the `header` slot renders in
 * BOTH modes. Web puts `UpdatePrompt` / `InstallPrompt` / `PairConfirmCardSlot`
 * there, and those are not content — they must survive a screen-share.
 *
 * SECURITY: this component is the screen-share-safe view. It renders app names
 * and counts only. Never add notification titles, bodies, sender names or
 * timestamps here — that is the entire point of the mode.
 */
import type { ConversationThread, DecryptedNotification } from "@reflecto/client-core";
import { BellOff01 } from "@untitledui/icons";
import { type ReactNode, useMemo } from "react";
import { base64ImageDataUrl } from "../../utils/format";
import { PrivacyAppCard } from "../PrivacyAppCard";
import { buildPrivacyGroups, splitNotificationFeed } from "../SidePanelNotificationList/helpers";
import styles from "./PrivacyModeList.module.css";

export interface PrivacyModeListLabels {
  /** aria-label for the list of per-app count summaries. */
  privacyAppList: string;
  /**
   * Formats the count line, e.g. "5 messages" / "1 call" / "3 notifications".
   * `kind` selects the noun; the host owns the plural rules (singular when
   * count === 1).
   */
  privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => string;
  /** Empty state — shared wording with the normal list, so hosts pass the same strings. */
  noNotificationsTitle: string;
  noNotificationsDesc: string;
}

export interface PrivacyModeListProps {
  notifications: DecryptedNotification[];
  conversations: ConversationThread[];
  /** packageName → base64 icon. Same cache the normal list uses. */
  appIconCache: Record<string, string>;
  /**
   * Renders above the list in both modes — host-owned banners, not content.
   * See the file header for why this is not gated on the mode.
   */
  header?: ReactNode;
  labels: PrivacyModeListLabels;
}

export function PrivacyModeList({
  notifications,
  conversations,
  appIconCache,
  header,
  labels,
}: PrivacyModeListProps) {
  const groups = useMemo(() => {
    const { callNotifications, regularNotifications } = splitNotificationFeed(notifications);
    return buildPrivacyGroups(callNotifications, regularNotifications, conversations);
  }, [notifications, conversations]);

  return (
    <div className={styles.container}>
      {header}
      <div className={styles.scrollArea}>
        {groups.length === 0 ? (
          <div className={styles.empty}>
            <BellOff01 size={48} className={styles.emptyIcon} aria-hidden="true" />
            <p className={styles.emptyTitle}>{labels.noNotificationsTitle}</p>
            <p className={styles.emptyDescription}>{labels.noNotificationsDesc}</p>
          </div>
        ) : (
          <ul className={styles.privacyList} aria-label={labels.privacyAppList}>
            {groups.map((g) => {
              const raw = appIconCache[g.packageKey];
              return (
                <PrivacyAppCard
                  key={g.packageKey}
                  appName={g.appName}
                  countLabel={labels.privacyCount(g.count, g.labelKind)}
                  iconDataUrl={raw ? base64ImageDataUrl(raw) : undefined}
                />
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
